import React, { useState } from "react";
import { JcCommonProps, JcUtils } from "../utils/JcUtils";
import { JcColumn, JcRow } from "./JcBox";
import { JcButton, JcButtonType } from "./button/JcButton";
import { JcClickableText } from "./JcClickableText";
import { JcField } from "./JcField";
import { JcFieldGroup } from "./JcFieldGroup";
import { JcLoadingIndicator } from "./JcLoadingIndicator";
import { JcNotice, JcNoticeSeverity } from "./JcNotice";
import { JcTextInput } from "./JcTextInput";

export interface JcSignInFormProps extends JcCommonProps {
    /** Signs in; rejects with an `Error` whose message is shown inline. */
    readonly onSignIn: (email: string, password: string) => Promise<void>;
    /** Sends a reset email; omit to hide "Forgot password?". */
    readonly onPasswordReset?: (email: string) => Promise<void>;
}

/**
 * Email + password sign-in form. Owns its submission state and shows errors
 * inline; post-auth navigation belongs to the page reacting to auth state.
 */
export const JcSignInForm = (props: JcSignInFormProps): React.ReactElement => {
    const { onSignIn, onPasswordReset } = props;
    const commonProps = JcUtils.commonProps(props, ["jc-sign-in-form"]);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetSentTo, setResetSentTo] = useState<string | null>(null);

    const clearTransientState = () => {
        setError(null);
        setResetSentTo(null);
    };

    const handleSubmit = async () => {
        clearTransientState();
        const trimmed = email.trim();
        if (!trimmed || !password) {
            setError("Please enter your email and password.");
            return;
        }
        if (!trimmed.includes("@")) {
            setError("Please enter a valid email address.");
            return;
        }
        setIsSubmitting(true);
        try {
            await onSignIn(trimmed, password);
        } catch (err) {
            setError(errorMessage(err));
            setIsSubmitting(false);
        }
    };

    const handlePasswordReset = async () => {
        if (onPasswordReset === undefined) {
            return;
        }
        clearTransientState();
        const trimmed = email.trim();
        if (!trimmed.includes("@")) {
            setError("Enter your email address above first, then click Forgot password again.");
            return;
        }
        setIsSubmitting(true);
        try {
            await onPasswordReset(trimmed);
            setResetSentTo(trimmed);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <JcColumn {...commonProps} alignCenter spacing>
            <JcFieldGroup className="jc-sign-in-form-fields">
                <JcField label="Email">
                    <JcTextInput
                        type="email"
                        value={email}
                        onChange={setEmail}
                        disabled={isSubmitting}
                        placeholder="you@example.com"
                        autoComplete="username"
                        onEnter={handleSubmit}
                    />
                </JcField>
                <JcField label="Password">
                    <JcTextInput
                        type="password"
                        value={password}
                        onChange={setPassword}
                        disabled={isSubmitting}
                        placeholder="Your password"
                        autoComplete="current-password"
                        onEnter={handleSubmit}
                    />
                </JcField>
            </JcFieldGroup>

            {resetSentTo && (
                <JcNotice message={`If an account exists for ${resetSentTo}, a password reset email has been sent.`} />
            )}
            {error && <JcNotice message={error} severity={JcNoticeSeverity.ERROR} />}

            <JcRow spacing>
                <JcButton onClick={handleSubmit} disabled={isSubmitting} buttonType={JcButtonType.PRIMARY}>
                    {isSubmitting ? "Signing in..." : "Sign In"}
                </JcButton>
            </JcRow>

            {onPasswordReset && <JcClickableText label="Forgot password?" onClick={handlePasswordReset} />}

            {isSubmitting && <JcLoadingIndicator />}
        </JcColumn>
    );
};

const errorMessage = (err: unknown): string => {
    if (err instanceof Error && err.message) return err.message;
    console.error("Unexpected sign-in error:", err);
    return "Something went wrong. Please try again.";
};
